import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT,
    LOW_DEMAND_MODE, HIGH_DEMAND_MODE, CONTINUOUS_MODE,
    NO_DANGEROUS_FAILURE,
    NO_SAFE_FAILURE,
    NO_ARCHITECTURAL_CONSTRAINTS,
    IEC_61508_1998,
    IEC_61508_2010,
    IEC_61511_2003,
    IEC_61511_2016,
    // ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,
} from "const"
import {
    SilSafetyFunction,
    SilSafetyFunctionCreate,
    SilSubsystem,
    SilSubsystemBlock,
} from "server/models"
import {
    StateType,
    SilArchitecturalConstraints,
    SilTarget,
    useInput,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    SilSafetyFunctionValidation,

    LoadSilSafetyFunctionById,
    LoadAccountPackageUsage,
    GetMaxSilSafetyFunctions,
    SaveSilSafetyFunction,
    SaveSilSubsystems,
    CalculateSafetyFunction,
    IncreaseSilCalculations,

    closeModal,
    setSilSafetyFunctionEdit,
    setSilSafetyFunctionReloadList,
    setSilSafetyFunctionReloadView,
    setSilSafetyFunctionReloadEdit,
    setSilSafetyFunctionSubsystemList,

    setSystemPackageUsage,
} from "client/store"
import {
    Container,
    PageForm,
    FormGroup,
    Label,
    Input,
    CKEditor,
    Feedback,
    CheckboxWrapper,
    RadioButton,
    SelectDropdown,
    SelectDropdownItem,
    Button,
    SpinnerIcon,

    ConfirmWrapper,
    Confirm,
    ConfirmTitle,
    ConfirmDescription,
    ConfirmBottom,
    Empty,
} from "client/components"

import FormatProbability from "client/pages/sil/format/probability"

import { getMaxProbability, getMaxStr } from "client/pages/sil/formulas/internal"
import Model from "./model"

interface EditSafetyFunctionProps {
    safetyFunction: SilSafetyFunction
}

let isMounted: boolean

let saveMode = 0
let makeCalculations = false

const Form = ({
    safetyFunction,
}: EditSafetyFunctionProps) => {
    const subsystemList = useSelector((state: StoreType) => state.silSafetyFunction.subsystemList)

    const baseRoute = useSelector((state: StoreType) => state.silSafetyFunction.baseRoute)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const packageUsage = useSelector((state: StoreType) => state.system.packageUsage)
    const userSettings = useSelector((state: StoreType) => state.system.userSettings)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const {
        defaultSafetyFunctionName,
        defaultCalculationMethodDangerous,
        defaultCalculationMethodSafe,
        defaultArchitecturalConstraints,
        defaultMode,
    } = userSettings.sil

    const [name, setName] = useInput(safetyFunction.id ? safetyFunction.name : defaultSafetyFunctionName)
    const [description, setDescription] = useState(safetyFunction.description)
    const [calculationMethodDangerous, setCalculationMethodDangerous] = useState(
        safetyFunction.id ? safetyFunction.calculationMethodDangerous : defaultCalculationMethodDangerous,
    )
    const [calculationMethodSafe, setCalculationMethodSafe] = useState(
        safetyFunction.id ? safetyFunction.calculationMethodSafe : defaultCalculationMethodSafe,
    )
    const [architecturalConstraints, setArchitecturalConstraints] = useState(
        safetyFunction.id ? safetyFunction.architecturalConstraints : defaultArchitecturalConstraints,
    )
    const [mode, setMode] = useState(safetyFunction.id ? safetyFunction.mode : defaultMode)

    // target SIL / PFD
    const [targetSil, setTargetSil] = useState(safetyFunction.targetSil)

    const maxProbability = getMaxProbability(mode, targetSil)
    const [targetPfd, setTargetPfd] = useState(
        safetyFunction.id && safetyFunction.targetPfd !== null ? safetyFunction.targetPfd : maxProbability.toString(),
    )

    // target STL / STR
    const [targetStl, setTargetStl] = useState(safetyFunction.targetStl || null)

    const { minStr, maxStr } = getMaxStr(targetStl)
    const [targetStr, setTargetStr] = useState(
        safetyFunction.id && safetyFunction.targetStr !== null ? safetyFunction.targetStr : maxStr.toString(),
    )

    const [validation, setValidation] = useState<SilSafetyFunctionValidation>(null)
    const [showConfirm, setShowConfirm] = useState(false)

    const [calculateState, setCalculateState] = useState<StateType>(IDLE)
    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)


    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onChangeArchitecturalConstraints = (newArchitecturalConstraints: SilArchitecturalConstraints) => {
        const newSubsystemList: SilSubsystem[] = []
        subsystemList.forEach((subsystem) => {
            newSubsystemList.push({
                ...subsystem,
                architecturalConstraints: newArchitecturalConstraints,
            })
        })

        setArchitecturalConstraints(newArchitecturalConstraints)
        dispatch(setSilSafetyFunctionSubsystemList(newSubsystemList))
    }

    const onChangeTargetSil = (newTargetSil: SilTarget) => {
        const newMaxProbability = getMaxProbability(mode, newTargetSil)
        setTargetSil(newTargetSil)
        setTargetPfd(newMaxProbability.toString())
    }

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            if (isMounted) dispatch(closeModal())
            return
        }

        const path = safetyFunction.id
            ? `${baseRoute}/${VIEW}/${safetyFunction.id}`
            : baseRoute

        forceRerenderContent(path, dispatch)
    }

    const onCancelConfirm = () => {
        setShowConfirm(false)
        setCalculateState(IDLE)
        setSaveState(IDLE)
        setApplyState(IDLE)
    }

    const onSave = () => {
        if (saveMode === 1) setCalculateState(PENDING)
        if (saveMode === 2) setSaveState(PENDING)
        if (saveMode === 3) setApplyState(PENDING)

        if (saveMode === 1) {
            LoadAccountPackageUsage(userData.accountId)
            .then((res) => {
                dispatch(setSystemPackageUsage(res.packageUsage))
                setShowConfirm(true)
            })
        } else {
            save()
        }
    }

    const save = () => {
        LoadSilSafetyFunctionById(safetyFunction.id, project.id).then((existingFunction) => {
            let isCalculated = safetyFunction.id ? safetyFunction.calculated : false

            if (
                existingFunction && (
                    existingFunction.calculationMethodDangerous !== calculationMethodDangerous
                    || existingFunction.calculationMethodSafe !== calculationMethodSafe
                    || existingFunction.mode !== mode
                    || existingFunction.architecturalConstraints !== architecturalConstraints
                    || existingFunction.targetSil !== targetSil
                    || existingFunction.targetPfd !== targetPfd
                    || existingFunction.targetStr !== targetStr
                )
            ) {
                isCalculated = false
            }

            // check changes of the model
            if (existingFunction) {
                if (existingFunction.subsystems.length !== subsystemList.length) {
                    isCalculated = false
                } else {
                    existingFunction.subsystems.every((subsystem1, subsystemIndex) => {
                        if (!isCalculated) return false // stop further check

                        const subsystem2 = subsystemList[subsystemIndex]
                        const subsystemKeys = Object.keys(subsystem1) as (keyof SilSubsystem)[]
                        subsystemKeys.every((subsystemKey) => {
                            if (!isCalculated) return false // stop further check

                            if (subsystemKey === "blocks") {
                                const blocks1 = subsystem1.blocks
                                const blocks2 = subsystem2.blocks
                                blocks1.every((block1, blockIndex) => {
                                    if (!isCalculated) return false // stop further check

                                    const block2 = blocks2[blockIndex]
                                    const blockKeys = Object.keys(block1) as (keyof SilSubsystemBlock)[]
                                    blockKeys.every((blockKey) => {
                                        if (!isCalculated) return false // stop further check

                                        if (blockKey !== "projectDevice") {
                                            if (block1[blockKey] !== block2[blockKey]) {
                                                isCalculated = false
                                            }
                                        }
                                        return true
                                    })
                                    return true
                                })
                            } else if (
                                subsystem1[subsystemKey] !== subsystem2[subsystemKey]
                                && subsystemKey !== "name"
                            ) {
                                isCalculated = false
                            }
                            return true
                        })
                        return true
                    })
                }
            }

            const newSafetyFunctionData: SilSafetyFunctionCreate = {
                projectId: safetyFunction.projectId || project.id,
                name,
                description,
                calculationMethodDangerous,
                calculationMethodSafe,
                mode,
                architecturalConstraints,
                targetSil,
                targetPfd,
                targetStl,
                targetStr,
                copy: safetyFunction.copy || 0,
                calculated: makeCalculations ? true : isCalculated,
            }

            const { maxSafetyFunctions } = GetMaxSilSafetyFunctions(packageSettings, userData)

            SaveSilSafetyFunction(
                safetyFunction.id,
                newSafetyFunctionData,
                maxSafetyFunctions,
                lang,
            ).then((resSafetyFunction) => {
                // save subsystems if they were changed
                const promiseSubsystemArray: Promise<any>[] = []
                if (
                    !resSafetyFunction.validation
                    && (existingFunction.subsystems !== subsystemList || !safetyFunction.id)
                ) {
                    promiseSubsystemArray.push(
                        SaveSilSubsystems(
                            project.id,
                            resSafetyFunction.newSafetyFunctionId || safetyFunction.id,
                            subsystemList,
                        ),
                    )
                }

                Promise.all(promiseSubsystemArray).then(() => {
                    // calculate safety function
                    const promiseCalculateArray: Promise<any>[] = []
                    if (!resSafetyFunction.validation) {
                        if (makeCalculations || isCalculated) {
                            promiseCalculateArray.push(
                                CalculateSafetyFunction(
                                    {
                                        ...newSafetyFunctionData,
                                        id: resSafetyFunction.newSafetyFunctionId || safetyFunction.id,
                                    },
                                    subsystemList,
                                ),
                            )
                        }
                        if (makeCalculations) {
                            promiseCalculateArray.push(
                                IncreaseSilCalculations(userData.accountId, dispatch),
                            )
                        }
                    }

                    Promise.all(promiseCalculateArray).then(() => {
                        if (isMounted) {
                            setValidation(resSafetyFunction.validation)
                            setCalculateState(IDLE)
                            setSaveState(IDLE)
                            setApplyState(IDLE)

                            if (resSafetyFunction.newSafetyFunctionId) {
                                replaceHistoryState(`${baseRoute}/${EDIT}/${resSafetyFunction.newSafetyFunctionId}`)
                            }
                        }
                        if (resSafetyFunction.validation) return

                        const updatedSafetyFunction: SilSafetyFunction = {
                            ...newSafetyFunctionData,
                            id: resSafetyFunction.newSafetyFunctionId || safetyFunction.id,
                        }
                        dispatch(setSilSafetyFunctionEdit(updatedSafetyFunction))

                        dispatch(setSilSafetyFunctionReloadList(false))
                        dispatch(setSilSafetyFunctionReloadView(false))
                        setTimeout(() => {
                            dispatch(setSilSafetyFunctionReloadList(true))
                            dispatch(setSilSafetyFunctionReloadView(true))
                        }, 0)

                        if (saveMode === 1 || saveMode === 2) onClose()
                        if (saveMode === 3) {
                            dispatch(setSilSafetyFunctionReloadEdit(false))
                            setTimeout(() => {
                                dispatch(setSilSafetyFunctionReloadEdit(true))
                            }, 0)
                        }
                    })
                })
            })
        })
    }

    let probabilityTitle = ""
    if (mode === LOW_DEMAND_MODE) probabilityTitle = lang.targetPfd
    if (mode === HIGH_DEMAND_MODE || mode === CONTINUOUS_MODE) probabilityTitle = lang.targetPfh

    const validationProbabilityValueBetween = lang.validationValueBetween.replace("{0}", "0").replace("{1}", maxProbability.toString())
    const showValidationProbabilityValueBetween = !targetPfd
        || !targetPfd.length
        || Number(targetPfd) > maxProbability

    const validationTargetStrValueBetween = minStr && maxStr
        ? lang.validationValueBetween.replace("{0}", minStr.toString()).replace("{1}", maxStr.toString())
        : lang.validationValueRequired
    const showValidationTargetStrValueBetween = targetStl && (
        !targetStr
        || !targetStr.length
        || Number(targetStr) <= minStr
        || Number(targetStr) > maxStr
    )

    let emptyBlocks = false
    let emptyName = false
    let noConstraintsCount = 0
    const calculationMethodNoFailure = calculationMethodDangerous === NO_DANGEROUS_FAILURE && calculationMethodSafe === NO_SAFE_FAILURE

    // model is hidden when both dangerous and safe are set to "no failure"
    if (!calculationMethodNoFailure) {
        subsystemList.forEach((subsystem) => {
            if (subsystem.name.trim().length === 0) emptyName = true
            if (subsystem.architecturalConstraints === NO_ARCHITECTURAL_CONSTRAINTS) noConstraintsCount += 1
            subsystem.blocks.forEach((block) => {
                if (block === null) {
                    emptyBlocks = true
                }
            })
        })
    }

    // all blocks must have "No architectural constraints" OR
    // if one block has any of the other architectural constraints then all blocks need to have one except "No architectural constraints".
    const architecturalConstraintsMissing = noConstraintsCount !== 0 && subsystemList.length !== noConstraintsCount

    const { maxCalculations, usedCalculations } = packageUsage

    let leftCalculations = ""
    if (maxCalculations === null) leftCalculations = lang.unlimitedCalculations
    else leftCalculations = lang.leftCalculations.replace("{0}", usedCalculations.toString()).replace("{1}", maxCalculations.toString())

    const disableSave = !subsystemList.length
        || emptyBlocks
        || emptyName
        || architecturalConstraintsMissing
        || (calculationMethodDangerous !== NO_DANGEROUS_FAILURE && showValidationProbabilityValueBetween)
        || (calculationMethodSafe !== NO_SAFE_FAILURE && showValidationTargetStrValueBetween)
        || calculationMethodNoFailure

    return (
        <Container>
            <PageForm>
                <Row>
                    <Col xs={12}>
                        <FormGroup>
                            <Label>{lang.name}</Label>
                            <Input type="text" value={name} onChange={setName} />
                            <Feedback>{validation && validation.name}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.description}</Label>
                            <CKEditor
                                data={description}
                                onChange={setDescription}
                            />
                            <Feedback>{validation && validation.description}</Feedback>
                        </FormGroup>
                        <FormGroup>
                            <Label>{lang.calculationMethod}</Label>
                            <CheckboxWrapper displayColumn>
                                <Row>
                                    <Col xs={6}>
                                        <Label style={{ margin: "0px 0px 8px 15px" }}>{lang.dangerousFailures}</Label>
                                        <RadioButton
                                            checked={calculationMethodDangerous === NO_DANGEROUS_FAILURE}
                                            onChange={() => setCalculationMethodDangerous(NO_DANGEROUS_FAILURE)}
                                            title={lang.noDangerousFailure}
                                        />
                                        <RadioButton
                                            checked={calculationMethodDangerous === IEC_61508_2010}
                                            onChange={() => {
                                                setCalculationMethodDangerous(IEC_61508_2010)
                                                setCalculationMethodSafe(NO_SAFE_FAILURE)
                                            }}
                                            title={lang.iec_61508_2010}
                                        />
                                        <RadioButton
                                            checked={calculationMethodDangerous === IEC_61508_1998}
                                            onChange={() => {
                                                setCalculationMethodDangerous(IEC_61508_1998)
                                                setCalculationMethodSafe(NO_SAFE_FAILURE)
                                            }}
                                            title={lang.iec_61508_1998}
                                        />
                                        <RadioButton
                                            checked={calculationMethodDangerous === VDI_VDE_2180_2019}
                                            onChange={() => {
                                                setCalculationMethodDangerous(VDI_VDE_2180_2019)
                                                setCalculationMethodSafe(NO_SAFE_FAILURE)
                                            }}
                                            title={lang.vdi_vde_2180_2019}
                                        />
                                        {
                                            // TODO: temporarily disabled
                                            /*
                                            <RadioButton
                                                checked={calculationMethodDangerous === ISA_TR84_2002}
                                                onChange={() => {
                                                    setCalculationMethodDangerous(ISA_TR84_2002)
                                                    setCalculationMethodSafe(NO_SAFE_FAILURE)
                                                }}
                                                title={lang.isa_tr84_2002}
                                            />
                                            */
                                        }
                                        <RadioButton
                                            checked={calculationMethodDangerous === ISA_TR84_2022}
                                            onChange={() => {
                                                setCalculationMethodDangerous(ISA_TR84_2022)
                                                setCalculationMethodSafe(NO_SAFE_FAILURE)
                                            }}
                                            title={lang.isa_tr84_2022}
                                        />
                                    </Col>
                                    <Col xs={6}>
                                        <Label style={{ margin: "0px 0px 8px 15px" }}>{lang.safeFailures}</Label>
                                        <RadioButton
                                            checked={calculationMethodSafe === NO_SAFE_FAILURE}
                                            onChange={() => setCalculationMethodSafe(NO_SAFE_FAILURE)}
                                            title={lang.noSafeFailure}
                                        />
                                        <RadioButton
                                            checked={calculationMethodSafe === ISA_TR84_2022}
                                            onChange={() => {
                                                setCalculationMethodSafe(ISA_TR84_2022)
                                                setCalculationMethodDangerous(NO_DANGEROUS_FAILURE)
                                            }}
                                            title={lang.isa_tr84_2022}
                                        />
                                    </Col>
                                </Row>
                            </CheckboxWrapper>
                            <Feedback>{validation && validation.calculationMethodDangerous}</Feedback>
                            <Feedback>{validation && validation.calculationMethodSafe}</Feedback>
                        </FormGroup>
                        {
                            calculationMethodDangerous !== NO_DANGEROUS_FAILURE && (
                                // display only for dangerous method
                                <FormGroup>
                                    <Label>{lang.architecturalConstraints}</Label>
                                    <CheckboxWrapper displayColumn>
                                        <RadioButton
                                            checked={architecturalConstraints === NO_ARCHITECTURAL_CONSTRAINTS}
                                            onChange={() => onChangeArchitecturalConstraints(NO_ARCHITECTURAL_CONSTRAINTS)}
                                            title={lang.noArchitecturalConstraints}
                                        />
                                        <RadioButton
                                            checked={architecturalConstraints === IEC_61508_2010}
                                            onChange={() => onChangeArchitecturalConstraints(IEC_61508_2010)}
                                            title={lang.iec_61508_2010}
                                        />
                                        <RadioButton
                                            checked={architecturalConstraints === IEC_61508_1998}
                                            onChange={() => onChangeArchitecturalConstraints(IEC_61508_1998)}
                                            title={lang.iec_61508_1998}
                                        />
                                        <RadioButton
                                            checked={architecturalConstraints === IEC_61511_2016}
                                            onChange={() => onChangeArchitecturalConstraints(IEC_61511_2016)}
                                            title={lang.iec_61511_2016}
                                        />
                                        <RadioButton
                                            checked={architecturalConstraints === IEC_61511_2003}
                                            onChange={() => onChangeArchitecturalConstraints(IEC_61511_2003)}
                                            title={lang.iec_61511_2003}
                                        />
                                    </CheckboxWrapper>
                                    <Feedback>{validation && validation.architecturalConstraints}</Feedback>
                                </FormGroup>
                            )
                        }
                        <FormGroup>
                            <Label>{lang.mode}</Label>
                            <CheckboxWrapper>
                                <RadioButton
                                    checked={mode === LOW_DEMAND_MODE}
                                    onChange={() => setMode(LOW_DEMAND_MODE)}
                                    title={lang.modeLowDemand}
                                />
                                <RadioButton
                                    checked={mode === HIGH_DEMAND_MODE}
                                    onChange={() => setMode(HIGH_DEMAND_MODE)}
                                    title={lang.modeHighDemand}
                                />
                                <RadioButton
                                    checked={mode === CONTINUOUS_MODE}
                                    onChange={() => setMode(CONTINUOUS_MODE)}
                                    title={lang.modeContinuous}
                                />
                            </CheckboxWrapper>
                            <Feedback>{validation && validation.mode}</Feedback>
                        </FormGroup>

                        {
                            calculationMethodDangerous !== NO_DANGEROUS_FAILURE && ([
                                <FormGroup key="targetSil">
                                    <Label>{lang.targetSil}</Label>
                                    <SelectDropdown title={targetSil ? lang[`sil_${targetSil}`] : <Empty />}>
                                        <SelectDropdownItem onClick={() => onChangeTargetSil(null)}><Empty /></SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => onChangeTargetSil(1)}>{lang.sil_1}</SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => onChangeTargetSil(2)}>{lang.sil_2}</SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => onChangeTargetSil(3)}>{lang.sil_3}</SelectDropdownItem>
                                        <SelectDropdownItem onClick={() => onChangeTargetSil(4)}>{lang.sil_4}</SelectDropdownItem>
                                    </SelectDropdown>
                                    <Feedback>{validation && validation.targetSil}</Feedback>
                                </FormGroup>,
                                <FormatProbability
                                    key="targetPfd"
                                    title={probabilityTitle}
                                    data={targetPfd}
                                    onChange={setTargetPfd}
                                    validation={
                                        (validation && validation.targetPfd)
                                        || (showValidationProbabilityValueBetween && validationProbabilityValueBetween)
                                    }
                                />,
                            ])
                        }
                        {
                            calculationMethodSafe !== NO_SAFE_FAILURE && ([
                                <FormGroup key="targetStl">
                                    <Label>{lang.targetStl}</Label>
                                    <Input
                                        type="text"
                                        value={targetStl || ""}
                                        onChange={(e) => {
                                            const parsed = parseInt(e.target.value, 10)
                                            if (!Number.isNaN(parsed) && parsed > 0) {
                                                setTargetStl(parsed)
                                                setTargetStr(getMaxStr(parsed).maxStr.toString())
                                            } else {
                                                setTargetStl(null)
                                                setTargetStr(null)
                                            }
                                        }}
                                    />
                                    <Feedback>{validation && validation.targetStl}</Feedback>
                                </FormGroup>,
                                targetStl !== null && (
                                    <FormatProbability
                                        key="targetStr"
                                        title={lang.targetStr}
                                        data={targetStr}
                                        onChange={setTargetStr}
                                        validation={
                                            (validation && validation.targetStr)
                                            || (showValidationTargetStrValueBetween && validationTargetStrValueBetween)
                                        }
                                    />
                                ),
                            ])
                        }
                    </Col>
                </Row>
            </PageForm>

            {
                !calculationMethodNoFailure && (
                    <Model
                        safetyFunctionId={safetyFunction.id}
                        calculationMethodDangerous={calculationMethodDangerous}
                        calculationMethodSafe={calculationMethodSafe}
                        architecturalConstraints={architecturalConstraints}
                    />
                )
            }

            <PageForm>
                <Row>
                    <Col xs={12}>
                        <Button
                            variant="success"
                            row
                            disabled={disableSave}
                            onClick={() => {
                                saveMode = 1
                                makeCalculations = true
                                onSave()
                            }}
                        >
                            {calculateState === PENDING ? <SpinnerIcon /> : lang.calculate}
                        </Button>
                        <Button
                            variant="success"
                            row
                            disabled={disableSave}
                            onClick={() => {
                                saveMode = 2
                                makeCalculations = false
                                onSave()
                            }}
                        >
                            {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                        </Button>
                        <Button
                            variant="success"
                            row
                            disabled={disableSave}
                            onClick={() => {
                                saveMode = 3
                                makeCalculations = false
                                onSave()
                            }}
                        >
                            {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                        </Button>
                        <Button row onClick={onClose}>{lang.cancel}</Button>
                    </Col>
                </Row>

                { emptyBlocks && <Feedback>{lang.emptyBlocksNotAllowed}</Feedback> }
                { emptyName && <Feedback>{lang.emptySubsystemNameNotAllowed}</Feedback> }
                { architecturalConstraintsMissing && <Feedback>{lang.architecturalConstraintsMissing}</Feedback> }
                { !calculationMethodNoFailure && !subsystemList.length && <Feedback>{lang.noSubsystemAdded}</Feedback> }
            </PageForm>

            <ConfirmWrapper show={showConfirm} onClose={onCancelConfirm}>
                <Confirm align="center">
                    <ConfirmTitle>{lang.confirmCalculation}</ConfirmTitle>
                    <ConfirmDescription align="start">{leftCalculations}</ConfirmDescription>

                    <ConfirmBottom>
                        <Button row onClick={onCancelConfirm}>{lang.cancel}</Button>
                        <Button
                            variant="success"
                            row
                            disabled={maxCalculations !== null && usedCalculations >= maxCalculations}
                            onClick={() => {
                                save()
                                setShowConfirm(false)
                            }}
                        >
                            {lang.confirm}
                        </Button>
                    </ConfirmBottom>
                </Confirm>
            </ConfirmWrapper>
        </Container>
    )
}
export default Form
